import * as Sentry from "@sentry/react";

interface DeviceDiagnostics {
  // Device Information
  userAgent: string;
  platform: string;
  deviceType: string;
  screenResolution: {
    width: number;
    height: number;
    devicePixelRatio: number;
    availableWidth: number;
    availableHeight: number;
  };
  colorDepth: number;
  pixelDepth: number;

  // Browser Information
  browserInfo: {
    name: string;
    version: string;
    language: string;
    languages: string[];
    cookieEnabled: boolean;
    doNotTrack: string | null;
    maxTouchPoints: number;
  };

  // Network Information
  networkInfo: {
    onLine: boolean;
    connectionType?: string;
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
    saveData?: boolean;
  };

  // Hardware Information
  hardware: {
    deviceMemory?: number;
    hardwareConcurrency: number;
    maxTouchPoints: number;
    pointerEvents: boolean;
    touchSupport: boolean;
  };

  // Performance Information
  performance: {
    timing: any;
    memory?: {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
    navigation: {
      type: number;
      redirectCount: number;
    };
  };

  // System Information
  system: {
    timezone: string;
    timezoneOffset: number;
    locale: string;
    timestamp: number;
    dateString: string;
  };

  // Local Storage Information
  localStorage: {
    available: boolean;
    itemCount: number;
    totalSize: number;
    items: Record<string, any>;
  };

  // Media Support
  mediaSupport: {
    audio: {
      mp3: boolean;
      wav: boolean;
      ogg: boolean;
      m4a: boolean;
    };
    video: {
      mp4: boolean;
      webm: boolean;
      ogg: boolean;
      avi: boolean;
    };
  };

  // Feature Detection
  features: {
    webWorkers: boolean;
    serviceWorkers: boolean;
    webSockets: boolean;
    webRTC: boolean;
    geolocation: boolean;
    notifications: boolean;
    vibration: boolean;
    battery: boolean;
    fullscreen: boolean;
    localStorage: boolean;
    sessionStorage: boolean;
    indexedDB: boolean;
    webGL: boolean;
    webAudio: boolean;
    deviceOrientation: boolean;
    deviceMotion: boolean;
    clipboard: boolean;
  };

  // Permissions
  permissions: {
    [key: string]: string;
  };

  // Error Information
  errors: string[];
}

// Helper function to get browser info
function getBrowserInfo(): DeviceDiagnostics["browserInfo"] {
  const nav = navigator;
  let browserName = "Unknown";
  let browserVersion = "Unknown";

  // Simple browser detection
  if (nav.userAgent.includes("Chrome")) {
    browserName = "Chrome";
    const match = nav.userAgent.match(/Chrome\/([^\s]+)/);
    browserVersion = match ? match[1] : "Unknown";
  } else if (nav.userAgent.includes("Firefox")) {
    browserName = "Firefox";
    const match = nav.userAgent.match(/Firefox\/([^\s]+)/);
    browserVersion = match ? match[1] : "Unknown";
  } else if (nav.userAgent.includes("Safari")) {
    browserName = "Safari";
    const match = nav.userAgent.match(/Safari\/([^\s]+)/);
    browserVersion = match ? match[1] : "Unknown";
  } else if (nav.userAgent.includes("Edge")) {
    browserName = "Edge";
    const match = nav.userAgent.match(/Edge\/([^\s]+)/);
    browserVersion = match ? match[1] : "Unknown";
  }

  return {
    name: browserName,
    version: browserVersion,
    language: nav.language,
    languages: Array.from(nav.languages || []),
    cookieEnabled: nav.cookieEnabled,
    doNotTrack: nav.doNotTrack,
    maxTouchPoints: nav.maxTouchPoints || 0,
  };
}

// Helper function to get network info
function getNetworkInfo(): DeviceDiagnostics["networkInfo"] {
  const nav = navigator as any;
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection;

  return {
    onLine: nav.onLine,
    connectionType: connection?.type,
    effectiveType: connection?.effectiveType,
    downlink: connection?.downlink,
    rtt: connection?.rtt,
    saveData: connection?.saveData,
  };
}

// Helper function to get hardware info
function getHardwareInfo(): DeviceDiagnostics["hardware"] {
  const nav = navigator as any;

  return {
    deviceMemory: nav.deviceMemory,
    hardwareConcurrency: nav.hardwareConcurrency || 0,
    maxTouchPoints: nav.maxTouchPoints || 0,
    pointerEvents: "PointerEvent" in window,
    touchSupport: "ontouchstart" in window,
  };
}

// Helper function to get performance info
function getPerformanceInfo(): DeviceDiagnostics["performance"] {
  const perf = performance as any;

  return {
    timing: perf.timing
      ? {
          navigationStart: perf.timing.navigationStart,
          loadEventEnd: perf.timing.loadEventEnd,
          domContentLoadedEventEnd: perf.timing.domContentLoadedEventEnd,
          responseEnd: perf.timing.responseEnd,
          requestStart: perf.timing.requestStart,
        }
      : null,
    memory: perf.memory
      ? {
          usedJSHeapSize: perf.memory.usedJSHeapSize,
          totalJSHeapSize: perf.memory.totalJSHeapSize,
          jsHeapSizeLimit: perf.memory.jsHeapSizeLimit,
        }
      : undefined,
    navigation: {
      type: perf.navigation?.type || 0,
      redirectCount: perf.navigation?.redirectCount || 0,
    },
  };
}

// Helper function to get local storage info
function getLocalStorageInfo(): DeviceDiagnostics["localStorage"] {
  try {
    const items: Record<string, any> = {};
    let totalSize = 0;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key);
        items[key] = value;
        totalSize += key.length + (value?.length || 0);
      }
    }

    return {
      available: true,
      itemCount: localStorage.length,
      totalSize,
      items,
    };
  } catch (error) {
    return {
      available: false,
      itemCount: 0,
      totalSize: 0,
      items: {},
    };
  }
}

// Helper function to check media support
function getMediaSupport(): DeviceDiagnostics["mediaSupport"] {
  const audio = document.createElement("audio");
  const video = document.createElement("video");

  return {
    audio: {
      mp3: !!(audio.canPlayType && audio.canPlayType("audio/mpeg;").replace(/no/, "")),
      wav: !!(audio.canPlayType && audio.canPlayType("audio/wav;").replace(/no/, "")),
      ogg: !!(audio.canPlayType && audio.canPlayType("audio/ogg;").replace(/no/, "")),
      m4a: !!(audio.canPlayType && audio.canPlayType("audio/mp4;").replace(/no/, "")),
    },
    video: {
      mp4: !!(video.canPlayType && video.canPlayType("video/mp4;").replace(/no/, "")),
      webm: !!(video.canPlayType && video.canPlayType("video/webm;").replace(/no/, "")),
      ogg: !!(video.canPlayType && video.canPlayType("video/ogg;").replace(/no/, "")),
      avi: !!(video.canPlayType && video.canPlayType("video/avi;").replace(/no/, "")),
    },
  };
}

// Helper function to detect features
function getFeatureSupport(): DeviceDiagnostics["features"] {
  return {
    webWorkers: typeof Worker !== "undefined",
    serviceWorkers: "serviceWorker" in navigator,
    webSockets: "WebSocket" in window,
    webRTC: "RTCPeerConnection" in window,
    geolocation: "geolocation" in navigator,
    notifications: "Notification" in window,
    vibration: "vibrate" in navigator,
    battery: "getBattery" in navigator,
    fullscreen: "requestFullscreen" in document.documentElement,
    localStorage: "localStorage" in window,
    sessionStorage: "sessionStorage" in window,
    indexedDB: "indexedDB" in window,
    webGL: "WebGLRenderingContext" in window,
    webAudio: "AudioContext" in window || "webkitAudioContext" in window,
    deviceOrientation: "DeviceOrientationEvent" in window,
    deviceMotion: "DeviceMotionEvent" in window,
    clipboard: "clipboard" in navigator,
  };
}

// Helper function to check permissions
async function getPermissions(): Promise<DeviceDiagnostics["permissions"]> {
  const permissions: DeviceDiagnostics["permissions"] = {};

  if ("permissions" in navigator) {
    const permissionNames = [
      "geolocation",
      "notifications",
      "camera",
      "microphone",
      "midi",
      "background-sync",
      "push",
      "clipboard-read",
      "clipboard-write",
    ];

    for (const permission of permissionNames) {
      try {
        const result = await navigator.permissions.query({ name: permission as any });
        permissions[permission] = result.state;
      } catch (error) {
        permissions[permission] = "unknown";
      }
    }
  }

  return permissions;
}

// Helper function to get device type
function getDeviceType(): string {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return "tablet";
  }
  if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return "mobile";
  }
  return "desktop";
}

// Main function to gather all device diagnostics
export async function gatherDeviceDiagnostics(): Promise<DeviceDiagnostics> {
  const errors: string[] = [];

  try {
    const diagnostics: DeviceDiagnostics = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      deviceType: getDeviceType(),
      screenResolution: {
        width: screen.width,
        height: screen.height,
        devicePixelRatio: window.devicePixelRatio || 1,
        availableWidth: screen.availWidth,
        availableHeight: screen.availHeight,
      },
      colorDepth: screen.colorDepth,
      pixelDepth: screen.pixelDepth,
      browserInfo: getBrowserInfo(),
      networkInfo: getNetworkInfo(),
      hardware: getHardwareInfo(),
      performance: getPerformanceInfo(),
      system: {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timezoneOffset: new Date().getTimezoneOffset(),
        locale: navigator.language,
        timestamp: Date.now(),
        dateString: new Date().toISOString(),
      },
      localStorage: getLocalStorageInfo(),
      mediaSupport: getMediaSupport(),
      features: getFeatureSupport(),
      permissions: await getPermissions(),
      errors,
    };

    return diagnostics;
  } catch (error) {
    errors.push(`Error gathering diagnostics: ${error}`);
    throw error;
  }
}

// Function to send diagnostics to Sentry
export async function sendDeviceDiagnosticsToSentry(additionalContext?: Record<string, any>): Promise<void> {
  try {
    const diagnostics = await gatherDeviceDiagnostics();

    // Send to Sentry with structured data
    Sentry.addBreadcrumb({
      category: "device-diagnostics",
      message: "Device diagnostics gathered",
      level: "info",
      data: {
        deviceType: diagnostics.deviceType,
        browserName: diagnostics.browserInfo.name,
        browserVersion: diagnostics.browserInfo.version,
        screenResolution: `${diagnostics.screenResolution.width}x${diagnostics.screenResolution.height}`,
        onLine: diagnostics.networkInfo.onLine,
        connectionType: diagnostics.networkInfo.connectionType,
      },
    });

    // Set user context
    Sentry.setUser({
      id: localStorage.getItem("UniqueId") || "unknown",
      username: localStorage.getItem("Code") || "unknown",
    });

    // Set additional context
    Sentry.setContext("device", {
      userAgent: diagnostics.userAgent,
      platform: diagnostics.platform,
      deviceType: diagnostics.deviceType,
      screenResolution: diagnostics.screenResolution,
      colorDepth: diagnostics.colorDepth,
    });

    Sentry.setContext("browser", diagnostics.browserInfo);
    Sentry.setContext("network", diagnostics.networkInfo);
    Sentry.setContext("hardware", diagnostics.hardware);
    Sentry.setContext("system", diagnostics.system);
    Sentry.setContext("features", diagnostics.features);
    Sentry.setContext("mediaSupport", diagnostics.mediaSupport);
    Sentry.setContext("localStorage", {
      available: diagnostics.localStorage.available,
      itemCount: diagnostics.localStorage.itemCount,
      totalSize: diagnostics.localStorage.totalSize,
      // Don't send sensitive data, just keys
      keys: Object.keys(diagnostics.localStorage.items),
    });

    if (additionalContext) {
      Sentry.setContext("additional", additionalContext);
    }

    // Send as a custom event
    Sentry.captureMessage("Device diagnostics collected on page load", {
      level: "info",
      tags: {
        event_type: "device_diagnostics",
        device_type: diagnostics.deviceType,
        browser_name: diagnostics.browserInfo.name,
        online_status: diagnostics.networkInfo.onLine ? "online" : "offline",
      },
      extra: {
        full_diagnostics: diagnostics,
      },
    });

    console.log("[Device Diagnostics] Sent to Sentry:", diagnostics);
  } catch (error) {
    console.error("[Device Diagnostics] Error sending to Sentry:", error);

    // Still try to send the error to Sentry
    Sentry.captureException(error, {
      tags: {
        event_type: "device_diagnostics_error",
      },
    });
  }
}

// Utility function to monitor ongoing system changes
export function setupDiagnosticsMonitoring(): () => void {
  const cleanupFunctions: Array<() => void> = [];

  // Monitor network changes
  const handleOnline = () => {
    Sentry.addBreadcrumb({
      category: "network",
      message: "Device came online",
      level: "info",
    });
  };

  const handleOffline = () => {
    Sentry.addBreadcrumb({
      category: "network",
      message: "Device went offline",
      level: "warning",
    });
  };

  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);

  cleanupFunctions.push(() => {
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
  });

  // Return cleanup function
  return () => {
    cleanupFunctions.forEach((cleanup) => cleanup());
  };
}

// Function to send diagnostics when an error occurs
export async function sendErrorDiagnosticsToSentry(error: Error, errorContext?: Record<string, any>): Promise<void> {
  try {
    const diagnostics = await gatherDeviceDiagnostics();

    Sentry.withScope((scope) => {
      // Set all diagnostic contexts
      scope.setContext("device", {
        userAgent: diagnostics.userAgent,
        platform: diagnostics.platform,
        deviceType: diagnostics.deviceType,
        screenResolution: diagnostics.screenResolution,
      });

      scope.setContext("browser", diagnostics.browserInfo);
      scope.setContext("network", diagnostics.networkInfo);
      scope.setContext("hardware", diagnostics.hardware);
      scope.setContext("system", diagnostics.system);
      scope.setContext("features", diagnostics.features);
      scope.setContext("mediaSupport", diagnostics.mediaSupport);

      // Set error-specific context
      if (errorContext) {
        scope.setContext("errorContext", errorContext);
      }

      // Set tags for filtering
      scope.setTags({
        error_type: "device_error",
        device_type: diagnostics.deviceType,
        browser_name: diagnostics.browserInfo.name,
        online_status: diagnostics.networkInfo.onLine ? "online" : "offline",
      });

      // Set user context
      scope.setUser({
        id: localStorage.getItem("UniqueId") || "unknown",
        username: localStorage.getItem("Code") || "unknown",
      });

      // Capture the error
      Sentry.captureException(error);
    });

    console.log("[Device Diagnostics] Error sent to Sentry with full diagnostics");
  } catch (diagnosticsError) {
    console.error("[Device Diagnostics] Failed to send error diagnostics:", diagnosticsError);
    // Still send the original error even if diagnostics fail
    Sentry.captureException(error);
  }
}
